import React, { useEffect, useMemo, useState, useCallback, useRef, memo } from "react";
import {
  ApiDelete,
  ApiGet,
  ApiPost,
  ApiPut,
} from "../../../helpers/API/ApiData";
import DataTable, { defaultThemes } from "react-data-table-component";
import CreateIcon from "@material-ui/icons/Create";
import DeleteIcon from "@material-ui/icons/Delete";
import { Button } from "react-bootstrap";
import { Modal } from "react-bootstrap";
import Dialog from "@material-ui/core/Dialog";
import Toolbar from "@material-ui/core/Toolbar";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Slide from "@material-ui/core/Slide";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Helpers
const getNewDate = (date) => {
  if (!date) return "-";
  const d = new Date(date);
  const month = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${month[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
};

const defaultForm = {
  title: "",
  description: "",
  duration: 60,
  meetingLink: "",
  calendlyEventId: "",
  type: "individual",
  planId: "",
  price: "",
  currency: "USD",
  isActive: true,
};

// Memoize FormField to prevent unnecessary re-renders
const FormField = memo(({ label, error, children }) => (
  <div className="form-group row">
    <label className="col-xl-3 col-lg-3 col-form-label">{label}</label>
    <div className="col-lg-9 col-xl-6">
      {children}
      {error && <small className="text-danger">{error}</small>}
    </div>
  </div>
), (prevProps, nextProps) => {
  return (
    prevProps.label === nextProps.label &&
    prevProps.error === nextProps.error &&
    prevProps.children === nextProps.children
  );
});

const Meeting = () => {
  const [meetings, setMeetings] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loadingTable, setLoadingTable] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [search, setSearch] = useState("");

  // Refs to track input elements
  const inputRefs = useRef({
    title: null,
    description: null,
    duration: null,
    type: null,
    planId: null,
    price: null,
    currency: null,
    calendlyEventId: null,
    meetingLink: null,
    isActive: null,
  });
  const activeInput = useRef(null);

  const loadMeetings = async () => {
    try {
      setLoadingTable(true);
      const res = await ApiGet("plan/meeting");
      const data = res?.data?.data || res?.data?.meetings || res?.data || [];
      setMeetings(data.filter((m) => !m.isDeleted));
      setFiltered(data.filter((m) => !m.isDeleted));
    } catch (err) {
      console.error("Failed to load meetings", err);
      toast.error("Failed to load meetings");
    } finally {
      setLoadingTable(false);
    }
  };

  const loadPlans = async () => {
    try {
      const res = await ApiGet("plan");
      const payload = res?.data?.payload?.allPlan || [];
      const clean = (Array.isArray(payload) ? payload : []).filter(
        (p) => p?.isActive && !p?.isDelete
      );
      setPlans(clean);
    } catch (err) {
      console.warn("Could not load plans (optional)", err);
    }
  };

  useEffect(() => {
    loadMeetings();
    loadPlans();
  }, []);

  useEffect(() => {
    if (!search) {
      setFiltered(meetings);
      return;
    }
    const v = search.toLowerCase();
    setFiltered(
      meetings.filter((m) => {
        return (
          m?.title?.toLowerCase().includes(v) ||
          m?.description?.toLowerCase().includes(v) ||
          m?.type?.toLowerCase().includes(v) ||
          m?.currency?.toLowerCase().includes(v) ||
          m?.planId?.planName?.toLowerCase?.()?.includes(v)
        );
      })
    );
  }, [search, meetings]);

  const validate = () => {
    const e = {};
    if (!form.title?.trim()) e.title = "Title is required";
    if (!form.type || !["individual", "plan"].includes(form.type)) {
      e.type = "Type must be 'individual' or 'plan'";
    }
    if (!form.duration || Number(form.duration) <= 0) {
      e.duration = "Duration must be > 0 minutes";
    }
    if (form.type === "plan") {
      if (!form.planId) e.planId = "Please select a plan";
    }
    if (form.type === "individual") {
      if (form.price === "" || Number(form.price) <= 0) {
        e.price = "Price must be greater than 0";
      }
      if (!form.currency?.trim()) e.currency = "Currency is required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const openAdd = () => {
    setForm(defaultForm);
    setErrors({});
    setIsEditing(false);
    setEditId(null);
    setFormOpen(true);
  };

  const openEdit = async (id) => {
    try {
      setSubmitting(true);
      const res = await ApiGet(`plan/meeting/${id}`);
      const m = res?.data?.data || res?.data || {};
      setForm({
        title: m?.title || "",
        description: m?.description || "",
        duration: m?.duration ?? 60,
        meetingLink: m?.meetingLink || "",
        calendlyEventId: m?.calendlyEventId || "",
        type: m?.type || "individual",
        planId: typeof m?.planId === "object" ? m?.planId?._id || "" : m?.planId || "",
        price: m?.price ?? "",
        currency: m?.currency || "USD",
        isActive: m?.isActive ?? true,
      });
      setErrors({});
      setIsEditing(true);
      setEditId(id);
      setFormOpen(true);
    } catch (err) {
      console.error("Failed to fetch meeting", err);
      toast.error("Failed to open meeting");
    } finally {
      setSubmitting(false);
    }
  };

  const closeForm = () => {
    setFormOpen(false);
    activeInput.current = null;
  };

  const submitForm = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      if (isEditing && editId) {
        const payload = { ...form };
        if (payload.type === "plan") {
          delete payload.price;
          delete payload.currency;
        } else {
          delete payload.planId;
        }
        await ApiPut(`plan/meeting/${editId}`, payload);
        toast.success("Meeting updated");
      } else {
        const payload = { ...form };
        if (payload.type === "plan") {
          delete payload.price;
          delete payload.currency;
        } else {
          delete payload.planId;
        }
        await ApiPost("plan/meeting", payload);
        toast.success("Meeting created");
      }
      closeForm();
      loadMeetings();
    } catch (err) {
      console.error("Save failed", err);
      toast.error("Failed to save meeting");
    } finally {
      setSubmitting(false);
    }
  };

  const askDelete = (id) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await ApiDelete(`plan/meeting/${deleteId}`);
      toast.success("Meeting deleted");
      setConfirmOpen(false);
      setDeleteId(null);
      loadMeetings();
    } catch (err) {
      console.error("Delete failed", err);
      toast.error("Failed to delete meeting");
    } finally {
      setDeleting(false);
    }
  };

  // Memoized input change handler
  const handleInputChange = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    // Restore focus if it was lost
    if (activeInput.current && inputRefs.current[field]) {
      inputRefs.current[field].focus();
    }
  }, []);

  // Memoized type change handler
  const handleTypeChange = useCallback((value) => {
    setForm((prev) => {
      if (value === "plan") {
        return { ...prev, type: value, price: "", currency: "USD" };
      } else {
        return { ...prev, type: value, planId: "" };
      }
    });
    // Restore focus to type select
    if (activeInput.current === "type" && inputRefs.current.type) {
      inputRefs.current.type.focus();
    }
  }, []);

  // Track focus
  const handleFocus = useCallback((field) => {
    activeInput.current = field;
  }, []);

  const columns = useMemo(
    () => [
      {
        name: "S.No.",
        cell: (row, index) => index + 1,
        width: "80px",
      },
      {
        name: "Title",
        selector: (row) => row.title,
        sortable: true,
        wrap: true,
      },
      {
        name: "Type",
        selector: (row) => row.type,
        sortable: true,
        width: "130px",
        cell: (row) => (
          <span className={`badge ${row.type === "plan" ? "badge-info" : "badge-success"}`}>
            {row.type}
          </span>
        ),
      },
      {
        name: "Plan",
        selector: (row) =>
          typeof row.planId === "object" ? row.planId?.planName : row.planId ? "Attached" : "-",
        sortable: true,
        wrap: true,
      },
      {
        name: "Price",
        selector: (row) =>
          row.type === "individual" ? `${row.currency || "USD"} ${row.price || 0}` : "-",
        sortable: true,
        width: "160px",
      },
      {
        name: "Duration",
        selector: (row) => `${row.duration || 0} min`,
        sortable: true,
        width: "140px",
      },
      {
        name: "Updated",
        selector: (row) => row.updatedAt,
        cell: (row) => <>{getNewDate(row.updatedAt)}</>,
        sortable: true,
        width: "160px",
      },
      {
        name: "Actions",
        cell: (row) => {
          return (
            <div className="d-flex">
              <div
                className="cursor-pointer mr-3"
                title="Edit"
                onClick={() => openEdit(row._id)}
              >
                <CreateIcon />
              </div>
              <div
                className="cursor-pointer"
                title="Delete"
                onClick={() => askDelete(row._id)}
              >
                <DeleteIcon />
              </div>
            </div>
          );
        },
        width: "140px",
      },
    ],
    []
  );

  const customStyles = {
    header: {
      style: {
        minHeight: "56px",
      },
    },
    headRow: {
      style: {
        borderTopStyle: "solid",
        borderTopWidth: "1px",
        borderTopColor: defaultThemes.default.divider.default,
      },
    },
    headCells: {
      style: {
        "&:not(:last-of-type)": {
          borderRightStyle: "solid",
          borderRightWidth: "1px",
          borderRightColor: defaultThemes.default.divider.default,
        },
      },
    },
    cells: {
      style: {
        "&:not(:last-of-type)": {
          borderRightStyle: "solid",
          borderRightWidth: "1px",
          borderRightColor: defaultThemes.default.divider.default,
        },
      },
    },
  };

  return (
    <>
      <ToastContainer />
      <div className="card p-1">
        <div className="p-2">
          <div className="row mb-4 pr-3">
            <div className="col d-flex justify-content-between">
              <h2 className="pl-3 pt-2">All Meetings</h2>
            </div>
            <div className="col">
              <div>
                <input
                  type="text"
                  className={`form-control form-control-lg form-control-solid `}
                  placeholder="Search meetings"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="cus-medium-button-style button-height">
              <button className="btn btnColor" onClick={openAdd}>
                Add Meeting
              </button>
            </div>
          </div>
        </div>

        <DataTable
          columns={columns}
          data={filtered}
          customStyles={customStyles}
          pagination
          progressPending={loadingTable}
          progressComponent={<div className="p-4">Loading…</div>}
          noDataComponent={<div className="p-4">No meetings found</div>}
          style={{ marginTop: "-3rem" }}
        />
      </div>

      <Modal show={confirmOpen} onHide={() => setConfirmOpen(false)}>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">Delete Meeting</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this meeting?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
            Cancel
          </Button>
          <Button variant="danger" disabled={deleting} onClick={handleDelete}>
            Delete
            {deleting && <span className="mx-3 spinner spinner-white"></span>}
          </Button>
        </Modal.Footer>
      </Modal>

      <Dialog fullScreen open={formOpen} onClose={closeForm} TransitionComponent={Transition}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={closeForm} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Toolbar>

        <div className="form ml-30">
          <div className="form-group row">
            <div className="col-lg-9 col-xl-6 offset-xl-3">
              <p
                style={{
                  fontSize: "14px",
                  color: "#6c757d",
                  fontStyle: "italic",
                  lineHeight: "1.6",
                }}
              >
                {isEditing ? (
                  <>
                    <strong style={{ fontSize: "16px", color: "#343a40" }}>
                      Edit Meeting:
                    </strong>{" "}
                    Update meeting details below. For <em>plan</em> meetings, select a plan. For{" "}
                    <em>individual</em> meetings, set a price & currency.
                  </>
                ) : (
                  <>
                    <strong style={{ fontSize: "16px", color: "#343a40" }}>
                      Create Meeting:
                    </strong>{" "}
                    Choose <em>type</em> = <b>plan</b> to attach to a plan, or <b>individual</b>{" "}
                    to sell as a one-off paid meeting.
                  </>
                )}
              </p>
            </div>
          </div>

          <FormField label="Title" error={errors.title}>
            <input
              type="text"
              className="form-control form-control-lg form-control-solid"
              value={form.title}
              onChange={(e) => handleInputChange("title", e.target.value)}
              onFocus={() => handleFocus("title")}
              ref={(el) => (inputRefs.current.title = el)}
            />
          </FormField>

          <FormField label="Description">
            <textarea
              className="form-control form-control-lg form-control-solid"
              rows={3}
              value={form.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              onFocus={() => handleFocus("description")}
              ref={(el) => (inputRefs.current.description = el)}
            />
          </FormField>

          <FormField label="Duration (minutes)" error={errors.duration}>
            <input
              type="number"
              className="form-control form-control-lg form-control-solid"
              value={form.duration}
              min={1}
              onChange={(e) => handleInputChange("duration", Number(e.target.value))}
              onFocus={() => handleFocus("duration")}
              ref={(el) => (inputRefs.current.duration = el)}
            />
          </FormField>

          <FormField label="Type" error={errors.type}>
            <select
              className="form-control form-control-lg form-control-solid"
              value={form.type}
              onChange={(e) => handleTypeChange(e.target.value)}
              onFocus={() => handleFocus("type")}
              ref={(el) => (inputRefs.current.type = el)}
            >
              <option value="individual">Individual (paid)</option>
              <option value="plan">Plan</option>
            </select>
          </FormField>

          {form.type === "plan" && (
            <FormField label="Attach Plan" error={errors.planId}>
              <select
                className="form-control form-control-lg form-control-solid"
                value={form.planId}
                onChange={(e) => handleInputChange("planId", e.target.value)}
                onFocus={() => handleFocus("planId")}
                ref={(el) => (inputRefs.current.planId = el)}
              >
                <option value="">-- Select Plan --</option>
                {plans.map((p) => (
                  <option key={p._id} value={p._id}>
                    {p.planName} — {p.planType}/{p.duration} — {p.planCost}
                  </option>
                ))}
              </select>
            </FormField>
          )}

          {form.type === "individual" && (
            <>
              <FormField label="Price" error={errors.price}>
                <input
                  type="number"
                  className="form-control form-control-lg form-control-solid"
                  value={form.price}
                  min={1}
                  onChange={(e) => handleInputChange("price", Number(e.target.value))}
                  onFocus={() => handleFocus("price")}
                  ref={(el) => (inputRefs.current.price = el)}
                />
              </FormField>

              <FormField label="Currency" error={errors.currency}>
                <input
                  type="text"
                  className="form-control form-control-lg form-control-solid"
                  value={form.currency}
                  onChange={(e) => handleInputChange("currency", e.target.value)}
                  onFocus={() => handleFocus("currency")}
                  ref={(el) => (inputRefs.current.currency = el)}
                />
              </FormField>
            </>
          )}

          <FormField label="Calendly Event ID">
            <input
              type="text"
              className="form-control form-control-lg form-control-solid"
              value={form.calendlyEventId}
              onChange={(e) => handleInputChange("calendlyEventId", e.target.value)}
              onFocus={() => handleFocus("calendlyEventId")}
              ref={(el) => (inputRefs.current.calendlyEventId = el)}
            />
          </FormField>

          <FormField label="Meeting Link">
            <input
              type="text"
              className="form-control form-control-lg form-control-solid"
              placeholder="Zoom/Google Meet link"
              value={form.meetingLink}
              onChange={(e) => handleInputChange("meetingLink", e.target.value)}
              onFocus={() => handleFocus("meetingLink")}
              ref={(el) => (inputRefs.current.meetingLink = el)}
            />
          </FormField>

          <FormField label="Active">
            <select
              className="form-control form-control-lg form-control-solid"
              value={form.isActive ? "true" : "false"}
              onChange={(e) => handleInputChange("isActive", e.target.value === "true")}
              onFocus={() => handleFocus("isActive")}
              ref={(el) => (inputRefs.current.isActive = el)}
            >
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </FormField>

          <div className="d-flex align-items-center justify-content-center mb-5">
            <button onClick={submitForm} className="btn btnColor mr-2" disabled={submitting}>
              <span>{isEditing ? "Update Meeting" : "Add Meeting"}</span>
              {submitting && <span className="mx-3 spinner spinner-white"></span>}
            </button>
            <button onClick={closeForm} className="btn btn-light">
              Cancel
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default Meeting;